import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, AlertTriangle, Lock, BadgeIndianRupee } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQS } from "@/data/content";

export function PricingCards({ pricing, onUpgrade, currentPlus, renew }) {
  if (!pricing) return null;
  const { free, plus } = pricing;
  const List = ({ items }) => <ul className="mt-5 space-y-2.5">{items.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-700"><Check size={16} className="shrink-0 text-[#00B94F]" />{f}</li>)}</ul>;
  return (
    <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
      <div className="card flex flex-col p-7" data-testid="plan-free">
        <p className="font-semibold">{free.name}</p>
        <p className="mt-2 text-4xl font-extrabold">₹{free.price}</p>
        <p className="mt-2 text-sm text-slate-500">{free.description}</p>
        <List items={free.features} />
        <Link to="/signup" className="btn-outline mt-auto w-full translate-y-0 pt-2.5" style={{ marginTop: 28 }} data-testid="plan-free-cta">Get Started Free</Link>
      </div>
      <div className="relative flex flex-col rounded-xl border-2 border-[#00B94F] bg-white p-7 shadow-[0_20px_40px_-20px_rgba(0,185,79,0.45)]" data-testid="plan-plus">
        <span className="absolute -top-3 left-6 rounded-md bg-[#00B94F] px-2.5 py-0.5 text-[11px] font-bold text-white">Most Popular</span>
        <p className="font-semibold">{plus.name}</p>
        <p className="mt-2 text-4xl font-extrabold" data-testid="plan-plus-price">₹{plus.price}</p>
        <p className="mt-2 text-sm text-slate-500">{plus.description} {plus.duration_days ? `Valid for ${plus.duration_days} days.` : ""}</p>
        <List items={plus.features} />
        {onUpgrade ? (
          <button onClick={onUpgrade} disabled={currentPlus} className="btn-green w-full" style={{ marginTop: 28 }} data-testid="plan-plus-cta">{currentPlus ? "You're on Plus" : renew ? `Renew for ₹${plus.price}` : `Upgrade for ₹${plus.price}`} <ArrowRight size={16} /></button>
        ) : (
          <Link to="/membership" className="btn-green w-full" style={{ marginTop: 28 }} data-testid="plan-plus-cta">Upgrade for ₹{plus.price} <ArrowRight size={16} /></Link>
        )}
      </div>
    </div>
  );
}

export function PricingSection({ pricing }) {
  return (
    <section className="container-x py-16" data-testid="pricing-section">
      <div className="mb-10 text-center">
        <h2 className="h-section">Start free. Upgrade when you're ready.</h2>
        <p className="mt-2 text-sm text-slate-500">Choose a plan that fits your goals. No hidden fees.</p>
      </div>
      <PricingCards pricing={pricing} />
    </section>
  );
}

export function TrustSection() {
  const items = [[ShieldCheck, "Verified employers", "Genuine job listings"], [AlertTriangle, "Report suspicious listings", "Help us keep the platform safe"], [Lock, "Privacy controls", "Your data, your choice"], [BadgeIndianRupee, "Clear salary & eligibility", "Know what to expect"]];
  return (
    <section className="container-x pb-16" data-testid="trust-section">
      <div className="mb-8 text-center">
        <h2 className="h-section">Your search should feel safe.</h2>
        <p className="mt-2 text-sm text-slate-500">We work to keep the platform genuine, transparent and secure.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(([I, t, s]) => (
          <div key={t} className="card flex items-center gap-4 p-5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#EAF8EF] text-[#00B94F]"><I size={20} /></span>
            <div><p className="text-sm font-semibold">{t}</p><p className="text-xs text-slate-500">{s}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FaqList({ limit }) {
  const list = limit ? FAQS.slice(0, limit) : FAQS;
  return (
    <Accordion type="single" collapsible className="grid gap-x-6 md:grid-cols-2" data-testid="faq-accordion">
      {list.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`} className="mb-3 rounded-lg border border-[#e3ece6] bg-white px-4">
          <AccordionTrigger className="text-left text-sm font-medium hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-sm text-slate-600">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function FaqSection() {
  return (
    <section className="container-x pb-16" data-testid="faq-section">
      <div className="mb-6 flex items-end justify-between">
        <h2 className="h-section">Frequently Asked Questions</h2>
        <Link to="/faq" className="flex items-center gap-1 text-sm font-semibold text-[#009a41]">View all FAQs <ArrowRight size={14} /></Link>
      </div>
      <FaqList />
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="bg-[#EAF8EF]" data-testid="final-cta">
      <div className="container-x flex flex-col items-start justify-between gap-8 py-14 md:flex-row md:items-center">
        <div>
          <h2 className="text-3xl font-extrabold sm:text-4xl">Your Next Chapter Starts Now.</h2>
          <p className="mt-2 text-sm text-slate-600">Join job seekers, employers and colleges on Jobfixnow.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/jobs" className="btn-green" data-testid="final-explore-btn">Explore Jobs <ArrowRight size={16} /></Link>
            <Link to="/signup" className="btn-outline" data-testid="final-signup-btn">Create Free Profile</Link>
          </div>
        </div>
        <img src="/images/logo-full.webp" width="560" height="165" loading="lazy" decoding="async" alt="Jobfixnow — We Find. You Grow." className="w-72 rounded-xl mix-blend-multiply" />
      </div>
    </section>
  );
}
